import { TicketStatus } from "@signalist/shared-types";

import { ROUTE_CONFIG } from "@/routes/config";

import { PermissionRoles } from "@/enum";

import { useHasPermissions } from "@/utils/useHasPermissions";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { useCountAllProspectsQuery } from "@/pages/Private/redux/list/list.api";
import { useGetSidebarTrackerListsQuery } from "@/pages/Private/redux/list/sidebar-tracker-lists.api";
import { useGetFeedCountsQuery } from "@/pages/Private/redux/feed/feed.api";
import { useGetDashboardQuery } from "@/pages/Private/redux/dashboard/dashboard.api";
import { useGetTicketsQuery } from "@/pages/Private/redux/tickets/tickets.api";
import { useGetStaffTicketStatsQuery } from "@/pages/Private/redux/tickets/tickets-admin.api";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { AssignStatus, InclusionFilterStatus, PriorityFilterStatus } from "@/enum/list.enum";

import { Order } from "@/types/types";

import { useComposeBadgeComposers } from "./useComposeBadgeComposers";

/**
 * The Feed page selects all tracker lists on nav entry (up to this limit), so the
 * badge fetches the same set to keep the count in parity with the page's "new" tab.
 */
const DEFAULT_TRACKER_LISTS_LIMIT = 100;

const baseSorting = {
	page: 1,
	limit: 10,
	searchValue: "",
	sorting: {
		sortBy: "createdAt",
		sortOrder: Order.DESC,
	},
	filter: {},
};

/** Counts above 99 collapse to the shared "99+" sentinel (100) used by SidebarItem. */
const cap = (value?: number): number | undefined =>
	value !== undefined && value > 99 ? 100 : value;

/**
 * Feed nav badge: the "new" tab count as if all tracker lists were selected, mirroring
 * the Feed page's default selection. Gated behind the TRACKER permission (as the Feed nav
 * item is) so non-tracker users make no extra requests.
 */
const useFeedNewCount = (): number | undefined => {
	const { hasPermissions } = useHasPermissions();
	const canSeeFeed = hasPermissions([PermissionRoles.TRACKER]);

	// Its own endpoint, not `getLeanLists`: that one feeds the overview tables' slice (PREC-4131).
	const { data: trackerLists } = useGetSidebarTrackerListsQuery(
		{ limit: DEFAULT_TRACKER_LISTS_LIMIT },
		{ skip: !canSeeFeed }
	);

	const listIds = trackerLists?.data.map((list) => list.id) ?? [];

	const { data: feedCounts } = useGetFeedCountsQuery(
		{ listIds, search: "", priorityFilterStatus: [], signalFilterStatus: [], filter: null },
		{ skip: !canSeeFeed || listIds.length === 0 }
	);

	return feedCounts?.data.new;
};

/**
 * Live badge counts for the Messages / Signals & Leads nav items, keyed by route so a
 * section can look up the count for an item regardless of which group it renders in.
 */
export const useSidebarCounts = (userId?: number): Record<string, number | undefined> => {
	// The inbox badge always reflects the total number of replies across the company
	// (My / Team / All tabs live inside the inbox itself).
	const { data: countAllProspects } = useCountAllProspectsQuery({
		...baseSorting,
		assignStatus: [AssignStatus.REPLIES],
		inclusionFilterStatus: [InclusionFilterStatus.ADDED],
		priorityFilterStatus: [PriorityFilterStatus.ALL],
	});

	const { data: countReadyToSend } = useCountAllProspectsQuery({
		...baseSorting,
		assignStatus: [AssignStatus.DRAFT],
		inclusionFilterStatus: [InclusionFilterStatus.ADDED],
		priorityFilterStatus: [PriorityFilterStatus.ALL_WITH_PRIORITY],
	});

	const composeComposers = useComposeBadgeComposers(userId);

	const { data: countCompose } = useCountAllProspectsQuery({
		...baseSorting,
		assignStatus: [AssignStatus.DRAFT],
		inclusionFilterStatus: [InclusionFilterStatus.ADDED],
		priorityFilterStatus: [PriorityFilterStatus.ALL_WITH_PRIORITY],
		specialType: "COMPOSE_LIST",
		composers: composeComposers,
	});

	// Feed badge — "new" count across all tracker lists (parity with the Feed page).
	const feedNewCount = useFeedNewCount();

	// Lead Lists badge — the dashboard's "new in the last 30 days" total, reused verbatim.
	const { data: dashboard } = useGetDashboardQuery();

	// Support badge — tickets waiting on the customer (PREC-3937). Its own one-row query, so the
	// number stays exact even though the Support page only fetches the first 100 tickets. Skipped
	// while the flag is off or still loading: the tickets routes answer 403 in that state.
	const isSupportActive = useIsFeatureActive(FeaturesCodes.SUPPORT_TICKETS);

	const { data: needsReply } = useGetTicketsQuery(
		{ page: 1, limit: 1, status: [TicketStatus.WAITING_ON_CUSTOMER] },
		{ skip: !isSupportActive }
	);

	// Tickets badge — staff tickets waiting on us (PREC-3939). One cache entry with the queue page's
	// strip; skipped for everyone without GLOBAL_ADMIN (the route answers 403 otherwise).
	const { hasPermissions: hasStaffPermissions } = useHasPermissions();
	const isStaff = hasStaffPermissions([PermissionRoles.GLOBAL_ADMIN]);

	const { data: staffStats } = useGetStaffTicketStatsQuery(undefined, { skip: !isStaff });

	return {
		[ROUTE_CONFIG.MESSAGE_CENTER_INBOX]: countAllProspects?.count,
		[ROUTE_CONFIG.MESSAGE_CENTER]: cap(countReadyToSend?.count),
		[ROUTE_CONFIG.MESSAGE_CENTER_COMPOSE]: cap(countCompose?.count),
		[ROUTE_CONFIG.FEED]: cap(feedNewCount),
		[ROUTE_CONFIG.LISTS]: cap(dashboard?.newDrafts.total),
		// `|| undefined` so a count of 0 renders no bubble at all.
		[ROUTE_CONFIG.SUPPORT]: cap(Number(needsReply?.meta.itemCount ?? 0) || undefined),
		[ROUTE_CONFIG.TICKETS]: cap(Number(staffStats?.waitingOnUs.count ?? 0) || undefined),
	};
};
